"use client";

import * as React from "react";
import { createPortal } from "react-dom";

import { POPUP_SESSION_CAP } from "@/lib/ads/config";
import type { GptRenderEndedEvent } from "@/lib/ads/gpt";
import { AdSlot } from "./ad-slot";
import { useAdRuntime } from "./ad-provider";
import { POPUP_DISMISS_EVENT } from "./public-api";

/**
 * Light box / interstitial shell for the PopUp unit — the legacy pages drew this shell
 * themselves (`ad_lightbox_setting.php` desktop, `ad_popup_setting.php` mobile) around a
 * plain image creative:
 *
 * - desktop / tablet: the creative (640x480 …) in a white panel with 20px padding
 *   (640x480 → 680x520), centred on a 70 % black backdrop
 * - phone: the 640x920 / 320x480 creative scaled to 90 % of the screen width, centred
 * - opens only after GPT rendered a creative (never an empty box); 500 ms fade
 * - closes on the ✕, a click / tap on the backdrop, Escape, `hkejAds.dismissPopup()`,
 *   and with the page (the slot is destroyed on navigation)
 * - shown on every page view like the legacy sites; `POPUP_SESSION_CAP` limits it to once per
 *   browser session (then the slot is not even requested)
 * - a 1x1 custom creative keeps drawing its own overlay: the shell stays hidden for it
 *
 * The AdSlot sits inside the hidden shell from mount (visibility, not display, so the
 * provider sees client rects and defines it; fixed in the viewport, so lazy-load fetches it
 * at once).
 */
const PAD = 20;
const SEEN_KEY = "ej_popup_seen";
const PREVIEW_SIZE = { desktop: [640, 480], tablet: [768, 1004], mobile: [640, 920] } as const;

export function PopUpOverlay({ label = "廣告" }: { label?: string }) {
  const rt = useAdRuntime();
  const [mounted, setMounted] = React.useState(false);
  const [capped, setCapped] = React.useState(false);
  const [phase, setPhase] = React.useState<"idle" | "open" | "closed">("idle");
  const [size, setSize] = React.useState<[number, number]>([640, 480]);
  const [k, setK] = React.useState(1);
  const stageRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMounted(true);
    if (POPUP_SESSION_CAP) {
      try { if (window.sessionStorage.getItem(SEEN_KEY)) setCapped(true); } catch { /* storage blocked */ }
    }
  }, []);

  const open = React.useCallback((w: number, h: number) => {
    setSize([w, h]);
    setPhase("open");
    if (POPUP_SESSION_CAP) { try { window.sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ } }
  }, []);
  const close = React.useCallback(() => setPhase((p) => (p === "open" ? "closed" : p)), []);

  // design review: `?adpreview=1` opens the shell around the band's tallest placeholder
  React.useEffect(() => {
    if (rt.preview) { const [w, h] = PREVIEW_SIZE[rt.device]; open(w, h); }
  }, [rt.preview, rt.device, open]);

  const onRender = React.useCallback((e: GptRenderEndedEvent) => {
    if (e.isEmpty) return;
    const z = e.size;
    const w = Array.isArray(z) ? Number(z[0]) : 0, h = Array.isArray(z) ? Number(z[1]) : 0;
    if (!(w > 20 && h > 20)) return; // 1x1 / fluid: a custom creative draws its own overlay
    // legacy: open only once GPT actually put an iframe on the page
    requestAnimationFrame(() => {
      if (stageRef.current?.querySelector('div[id^="google_ads_iframe_"] iframe')) open(w, h);
    });
  }, [open]);

  /* scale: whole creative on screen, phones at 90 % of the width (legacy), never enlarged —
     kept current while hidden too, so the panel never exceeds the viewport */
  React.useEffect(() => {
    const fit = () => {
      const [w, h] = size;
      setK(Math.min(1, (0.9 * window.innerWidth - PAD * 2) / w, (0.9 * window.innerHeight - PAD * 2) / h));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [size]);

  /* Escape + the public API */
  React.useEffect(() => {
    if (phase !== "open") return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    window.addEventListener(POPUP_DISMISS_EVENT, close);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener(POPUP_DISMISS_EVENT, close); };
  }, [phase, close]);

  if (!mounted || capped) return null;
  const [w, h] = size;
  return createPortal(
    <div
      className="hkej-ad-popup"
      data-ad-container="PopUp"
      data-ad-overlay-state={phase}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      aria-hidden={phase !== "open"}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="hkej-ad-popup-panel" style={{ width: Math.round(w * k) + PAD * 2, height: Math.round(h * k) + PAD * 2 }}>
        <button type="button" className="hkej-ad-popup-close" aria-label="關閉廣告" onClick={close}>
          {label} ✕
        </button>
        <div ref={stageRef} className="hkej-ad-popup-stage" style={{ width: w, height: h, transform: `scale(${k})` }}>
          <AdSlot unit="PopUp" onRender={onRender} className="hkej-ad-popup-slot" />
        </div>
      </div>
    </div>,
    document.body,
  );
}
